import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { devApi, prodDevApi } from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IGetVirtualAccountDetailsResponse {
  status?: boolean;
  message?: string;
  statusCode?: string;
  data?: {
    accountBalance: number;
    accountNumber: string;
    accountName: string;
    businessId: string;
    accountType: string;
    autoPayoutEnabled: boolean;
    requestReference: string;
    isSinglePayment: boolean;
    status: string;
  };
}

const initialState: IApiState<IGetVirtualAccountDetailsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getVirtualAccountDetailsAsync = createAsyncThunk(
  'virtualAccountDetails',
  async (
    { accountNumber }: { accountNumber: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const devTokenInStorage = localStorage.getItem('devTokenData');

      const parsedDevTokenInStorage = devTokenInStorage
        ? JSON.parse(devTokenInStorage)
        : null;

      const developerId = localStorage.getItem('developerId');

      const environment = state.environment?.value;

      const apiInstance = environment === 'live' ? prodDevApi : devApi;

      const authToken =
        environment === 'live' ? accessToken : parsedDevTokenInStorage;

      const url = `/virtual-accounts/${accountNumber}`;

      const config = {
        headers: {
          Authorization: `Token ${authToken}`,
          'Business-id': developerId,
          Environment: 'live',
        },
      };

      const { data } = await apiInstance.get(url, config);

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const virtualAccountDetailsGetSlice = createSlice({
  name: 'virtualAccountDetails/get',
  initialState,
  reducers: {
    resetGetVirtualAccountDetails: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVirtualAccountDetailsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getVirtualAccountDetailsAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetVirtualAccountDetailsResponse;
      })
      .addCase(getVirtualAccountDetailsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetVirtualAccountDetailsResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetGetVirtualAccountDetails } =
  virtualAccountDetailsGetSlice.actions;

export default virtualAccountDetailsGetSlice.reducer;
